
import { useState } from 'react'
import { type AltTextResponse } from '../types'

interface Props {
  apiKey: string
}

export default function BatchUploader({ apiKey }: Props) {
  const [imageUrls, setImageUrls] = useState<string[]>([''])
  const [mode, setMode] = useState<'short' | 'long' | 'seo'>('short')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<AltTextResponse[]>([])

  // Validate API key on mount
  if (!apiKey || !apiKey.trim() || !apiKey.startsWith('atg_')) {
    return (
      <div className="glass-card rounded-xl p-6">
        <div className="text-center text-red-600 dark:text-red-400">
          <p>API key is missing or invalid. Please refresh the page or contact support.</p>
        </div>
      </div>
    )
  }

  const addImageUrl = () => {
    setImageUrls([...imageUrls, ''])
  }

  const updateImageUrl = (index: number, value: string) => {
    const newUrls = [...imageUrls]
    newUrls[index] = value
    setImageUrls(newUrls)
  }

  const removeImageUrl = (index: number) => {
    setImageUrls(imageUrls.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setResults([])

    const validUrls = imageUrls.filter(url => url.trim() !== '')
    
    if (validUrls.length === 0) {
      alert('Please add at least one image URL')
      setLoading(false)
      return
    }

    // Validate API key
    if (!apiKey || !apiKey.trim()) {
      alert('API key is missing. Please refresh the page or contact support.')
      setLoading(false)
      return
    }

    if (!apiKey.startsWith('atg_')) {
      alert('Invalid API key format. Please refresh the page or contact support.')
      setLoading(false)
      return
    }

    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://eutekssvzhtkwzperkzb.supabase.co/functions/v1'
      // Use /batch-public for Supabase direct, /batch for Vercel API
      const batchEndpoint = apiBaseUrl.includes('supabase.co') ? '/batch-public' : '/batch'
      const requestBody = {
        api_key: apiKey.trim(), // Send API key in body for public endpoints
        images: validUrls.map(url => ({
          image_url: url.trim(),
          mode,
          source: 'batch', // Track that this is from batch processing
        })),
      }

      const response = await fetch(`${apiBaseUrl}${batchEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error', message: `HTTP ${response.status}` }))
        throw new Error(error.message || error.error || 'Failed to generate alt text')
      }

      const data = await response.json()
      setResults(data.results || [])
    } catch (error: any) {
      alert(error.message || 'An error occurred while processing the batch upload')
    } finally {
      setLoading(false)
    }
  }

  const downloadCSV = () => {
    const csv = [
      ['Image URL', 'Alt Text'].join(','),
      ...results.map((r) =>
        [r.image_url, r.alt_text]
          .map((v) => `"${(v ?? '').replace(/"/g, '""')}"`)
          .join(',')
      ),
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `alt-text-results-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const downloadJSON = () => {
    const json = JSON.stringify(results, null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `alt-text-results-${new Date().toISOString().split('T')[0]}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const successful = results.filter(r => r.alt_text).length
  const failed = results.length - successful

  return (
    <div className="glass-card rounded-xl p-6 hover-lift">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
          <svg className="w-6 h-6 mr-2 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          Batch Alt Text Generator
        </h3>
        {results.length > 0 && (
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {successful} successful, {failed} failed
            </span>
          </div>
        )}
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Mode Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Alt Text Mode
          </label>
          <div className="flex space-x-3">
            {(['short', 'long', 'seo'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={`flex-1 px-4 py-3 rounded-lg font-medium transition hover-lift ${
                  mode === m
                    ? 'bg-primary-600 dark:bg-primary-500 text-white shadow-lg'
                    : 'bg-white/50 dark:bg-slate-800/50 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-slate-700'
                }`}
              >
                {m.charAt(0).toUpperCase() + m.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Image URLs */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Image URLs
          </label>
          <div className="space-y-3">
            {imageUrls.map((url, index) => (
              <div key={index} className="flex space-x-2 animate-fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => updateImageUrl(index, e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="flex-1 px-4 py-2 border border-gray-300/50 dark:border-slate-600/50 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500"
                />
                {imageUrls.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeImageUrl(index)}
                    className="px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition hover-lift"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addImageUrl}
              className="w-full px-4 py-2 border-2 border-dashed border-gray-300/50 dark:border-slate-600/50 text-gray-600 dark:text-gray-400 rounded-lg hover:border-primary-500 dark:hover:border-primary-400 hover:text-primary-600 dark:hover:text-primary-400 transition flex items-center justify-center space-x-2 hover-lift"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Add another URL</span>
            </button>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading || imageUrls.every(url => !url.trim())}
          className="w-full px-6 py-3 bg-primary-600 dark:bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-700 dark:hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition hover-lift flex items-center justify-center space-x-2"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Generating Alt Text...</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span>Generate Alt Text</span>
            </>
          )}
        </button>
      </form>

      {/* Results */}
      {results.length > 0 && (
        <div className="mt-8 pt-6 border-t border-gray-200/50 dark:border-slate-700/50 animate-fade-in">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Results</h4>
            <div className="flex space-x-2">
              <button
                onClick={downloadCSV}
                className="px-4 py-2 bg-white/50 dark:bg-slate-800/50 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-white dark:hover:bg-slate-700 text-sm font-medium transition hover-lift flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>CSV</span>
              </button>
              <button
                onClick={downloadJSON}
                className="px-4 py-2 bg-white/50 dark:bg-slate-800/50 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-white dark:hover:bg-slate-700 text-sm font-medium transition hover-lift flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>JSON</span>
              </button>
            </div>
          </div>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {results.map((result, index) => (
              <div 
                key={index} 
                className={`border rounded-lg p-4 transition hover-lift animate-fade-in ${
                  result.alt_text 
                    ? 'border-green-200/50 dark:border-green-800/50 bg-green-50/50 dark:bg-green-900/20' 
                    : 'border-red-200/50 dark:border-red-800/50 bg-red-50/50 dark:bg-red-900/20'
                }`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate mb-1">
                      {result.image_url}
                    </div>
                    <div className={`font-medium ${
                      result.alt_text 
                        ? 'text-gray-900 dark:text-white' 
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {result.alt_text || 'Failed to generate'}
                    </div>
                  </div>
                  {result.alt_text && (
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(result.alt_text || '')
                        alert('Copied to clipboard!')
                      }}
                      className="ml-2 p-1 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition"
                      title="Copy to clipboard"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
